import type { LatLng } from "../types";

/**
 * Single source of truth for "can this point go on a map?".
 *
 * Domain types declare lat/lng as `number`, but the API delivers null for
 * spaces whose address was never geocoded. Exact (0,0) also means missing
 * data in this product (Brazilian real estate is never at Null Island) —
 * same rule as Vision360Map.
 */
export const isMappable = (point: LatLng): boolean =>
  point.lat != null &&
  point.lng != null &&
  !Number.isNaN(point.lat) &&
  !Number.isNaN(point.lng) &&
  !(point.lat === 0 && point.lng === 0);

export const mappableSpaces = <T extends LatLng>(items: T[]): T[] =>
  items.filter(isMappable);

/**
 * Average center of the mappable spaces only, or null when there is nothing
 * to map — callers must not fall back to a null-coerced (0,0) center.
 */
export function averageCenter(points: readonly LatLng[]): LatLng | null {
  const valid = points.filter(isMappable);
  if (valid.length === 0) return null;
  return {
    lat: valid.reduce((sum, p) => sum + p.lat, 0) / valid.length,
    lng: valid.reduce((sum, p) => sum + p.lng, 0) / valid.length,
  };
}

export const coordKey = (lat: number, lng: number): string =>
  `${lat.toFixed(4)},${lng.toFixed(4)}`;
